import './SocialSharingModal.scss';

import closeIcon from 'assets/svgs/close.svg';
import downloadIcon from 'assets/svgs/download.svg';
import linkIcon from 'assets/svgs/link.svg';
import mailSent from 'assets/svgs/mail-sent.svg';
import classNames from 'classnames';
import SocialSharingButtonsList from 'components/Button/SocialShareButtonsList/SocialShareButtonsList';
import SocialSharingCarousel from 'components/Modals/SocialSharingModal/SocialSharingCarousel';
import TextBox from 'components/Modals/SocialSharingModal/TextBox/TextBox';
import NotFound from 'components/NotFound/NotFound';
import { SocialShareModalContext } from 'contexts/SocialShareModalContext';
import useGetSocialShareButtons from 'hooks/socialShare/useGetSocialShareButtons';
import { observer } from 'mobx-react-lite';
import { IconImage } from 'petdna-lib-uicomponents/components';
import { buildTemplateForContext } from 'petdna-lib-uicomponents/helpers';
import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useStores } from 'stores/useStores';

import WebShare from '../WebShare/WebShare';
import SocialUpdatePhoto from './SocialUpdatePhoto/SocialUpdatePhoto';

const IconButton: React.FC<{
  text: string;
  iconSrc?: string;
  action?: any;
  children?: React.ReactNode;
}> = ({
  iconSrc, text, action, children,
}) => (
  <div className="icon-button-wrapper">
    {children || (
      <button type="button" className="btn round-6 share-btn" onClick={action}>
        <img height="24" width="24" src={iconSrc} alt={text} />
      </button>
    )}
    <p className="ms-1 icon-button-text">{text}</p>
  </div>
);

const SocialSharingModal: React.FC = observer(() => {
  const { t } = useTranslation();
  const [isUpdatePhotoModeOpen, setIsUpdatePhotoOpen] = useState<boolean>(false);
  const { socialShareButtonList } = useGetSocialShareButtons();
  const context = useContext(SocialShareModalContext);

  const modalRef = useRef<HTMLDivElement>(null);
  const { testsStore, breedStore } = useStores();
  const { breedResults } = breedStore;

  const { shareSheetText, petProfile } = testsStore;

  if (!context) {
    throw new Error(
      'SocialShareModal must be used within a SocialShareModalProvider!.',
    );
  }

  const {
    isOpen,
    closeModal,
    shareUrl,
    sharedImages,
    downloadImage,
    error,
    sharedImageFile,
    setCurrentTemplate,
    loadShareData,
    isLoading,
    setIsLoading,
  } = context;

  const shareText = useMemo(() => {
    if (!shareSheetText?.text.text) return '';

    const breedsString = breedResults?.breeds
      ?.map((breed) => `${breed.percentage}% ${breed.name}`)
      .join(', ');

    const petDetails = {
      PET_NAME: petProfile?.name,
      BREEDS: breedsString,
    };

    return buildTemplateForContext(shareSheetText?.text.text, petDetails);
  }, [breedResults?.breeds, petProfile?.name, shareSheetText?.text.text]);

  const handleClickOutside = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (modalRef.current === e.target) {
        closeModal();
      }
    },
    [closeModal],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    },
    [closeModal],
  );

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        toast.success('Image URL copied to clipboard!');
      })
      .catch((err) => {
        toast.error('Failed to copy Image URL to clipboard!');
      });
  };

  const toggleIsUpdatePhotoOpen = useCallback(() => {
    setIsUpdatePhotoOpen((prev) => !prev);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="modal-social-share show d-block"
      tabIndex={-1}
      onClick={handleClickOutside}
      onKeyDown={handleKeyDown}
      ref={modalRef}
      role="presentation"
    >
      {(!isLoading && sharedImageFile) ? (
        <div className="modal-social-share-dialog">
          <div className={classNames('modal-social-share-content', isUpdatePhotoModeOpen ? 'overflow-hidden' : '')}>
            <button type="button" className="btn-close" onClick={closeModal}>
              <IconImage className="close-icon" src={closeIcon} />
            </button>
            <div className="modal-social-share-body">
              <SocialUpdatePhoto
                isUpdatePhotoModeOpen={isUpdatePhotoModeOpen}
                onClose={closeModal}
                toggleIsUpdatePhotoOpen={toggleIsUpdatePhotoOpen}
                loadShareData={loadShareData}
                setIsLoading={setIsLoading}
              />
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-6 col-md-6 col-sm-12 social-sharing-carousel-wrapper">
                    <div className="update-photo-btn-wrapper">
                      <button
                        type="button"
                        className="btn btn-primary-text update-photo-btn"
                        onClick={toggleIsUpdatePhotoOpen}
                      >
                        {t('socialSharingModal.updatePhoto')}
                      </button>
                    </div>
                    <SocialSharingCarousel
                      sharedImages={sharedImages}
                      setCurrentTemplate={setCurrentTemplate}
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 modal-social-share-text">
                    <div className="row">
                      <div className="col-12">
                        <h3 className="h1 d-none d-md-block modal-social-share-title">
                          {shareSheetText?.title}
                        </h3>
                      </div>
                      <div className="col-lg-12 d-none d-md-flex flex-column gap-16">
                        <IconButton
                          iconSrc={downloadIcon}
                          text="Save image to device"
                          action={downloadImage}
                        />
                        <IconButton
                          iconSrc={linkIcon}
                          text="Copy Link"
                          action={handleCopyLink}
                        />
                        <div className="d-flex gap-32">
                          <IconButton text="Share to...">
                            <WebShare
                              text={shareText}
                              url={shareUrl}
                              {...(sharedImageFile && {
                                files: [sharedImageFile],
                              })}
                            >
                              <img
                                height="24"
                                width="24"
                                className="mail-sen-align"
                                src={mailSent}
                                alt="Share to"
                              />
                            </WebShare>
                          </IconButton>
                          {socialShareButtonList && (
                          <SocialSharingButtonsList
                            shareUrl={shareUrl}
                            text={shareText}
                            socialShareButtonList={socialShareButtonList}
                          />
                          )}
                        </div>
                      </div>
                      <div className="col-12 text-box-block">
                        <TextBox
                          content={shareSheetText?.text}
                          breedResults={breedResults}
                          petName={petProfile?.name}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="container mt-12 d-sm-block d-md-none social_share_modal_btn_container"
              >
                <div className="row">
                  <div className="col d-flex justify-content-center align-items-center flex-column">
                    <button
                      type="button"
                      onClick={downloadImage}
                      className="btn btn-info round-6 share-btn"
                    >
                      <img
                        height="24"
                        width="24"
                        src={downloadIcon}
                        alt="Download breed chart"
                      />
                    </button>
                    <p className="mt-16">{t('socialSharingModal.download')}</p>
                  </div>
                  <div className="col d-flex justify-content-center align-items-center flex-column">
                    <WebShare text={shareText} url={shareUrl}>
                      <img
                        height="24"
                        width="24"
                        className="mail-sen-align"
                        src={mailSent}
                        alt="Share to"
                      />
                    </WebShare>
                    <p className="mt-16">{t('socialSharingModal.shareTo')}</p>
                  </div>
                  <div className="col d-flex justify-content-center align-items-center flex-column">
                    <button
                      type="button"
                      onClick={handleCopyLink}
                      className="btn btn-info round-6 share-btn"
                    >
                      <img height="24" width="24" src={linkIcon} alt="Copy link" />
                    </button>
                    <p className="mt-16">{t('socialSharingModal.copyLink')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="modal-social-share-dialog">
          <div className="modal-social-share-content">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
              }}
            >
              {!error ? (
                <>
                  <span
                    className="spinner-border spinner-border-lg  "
                    aria-hidden="true"
                  />
                  <span className="mt-12" role="status">
                    Generating image....
                  </span>
                </>
              ) : (
                <NotFound />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default SocialSharingModal;
